// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Chip, DataTable, FieldSearchSelect, RemoteTable, Segmented } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetSessionByIdQuery,
  useGetSessionsByCommunityIdPaginationQuery,
} from "state/sessions/sessionApiSlice";

// Utils
import { TIME_QUARTERS } from "utils/constants/time";
import { useGetMembers, useGetMeters, useGetResources } from "utils/crud/read";
import { browserLocale, datetimeToTime, formatDate } from "utils/time";

const LIMIT = 9;

const fmt = (v, d = 6) =>
  Number.isFinite(Number(v))
    ? Number(v).toLocaleString(browserLocale(), { maximumFractionDigits: d })
    : "—";

const num = (v) => <span className="tabular-nums">{fmt(v)}</span>;

// Status → Chip tone, mirroring the legacy badge colours.
const statusTone = (status) => {
  if (status === "FAILED" || String(status).includes("ERROR")) return "red";
  if (status === "NO_TRANSACTIONS" || status === "NO MARKET") return "gray";
  if (status === "SETTLED") return "teal";
  if (status === "RUNNING") return "info";
  if (status === "DONE") return "green";
  return "gray";
};

/* Offers / transactions for an expanded market session, fetched from the
   per-session endpoint and shown in the rec-styled detail card. */
function MarketSessionDetail({ communityId, session, members, resources, meters, onClose }) {
  const { t } = useTranslation(["market", "common"]);
  const [detailTab, setDetailTab] = useState("offers");

  useEffect(() => {
    setDetailTab("offers");
  }, [session.id]);

  const { data, isFetching } = useGetSessionByIdQuery({
    sessionId: session.id,
    communityId,
    excludeDeleted: "false",
  });

  const memberName = (id) => members?.find((m) => m.id === id)?.name || "—";
  const resourceName = (id) => resources?.find((r) => r.id === id)?.name || "—";
  const meterId = (id) => meters?.find((m) => m.id === id)?.identifier || "—";

  const offerRows = useMemo(
    () =>
      (Array.isArray(data?.offers) ? data.offers : []).map((o, i) => ({
        id: i,
        meter: meterId(o?.meter_id),
        type: o?.type || "—",
        price: o?.price,
        energy: o?.energy,
        matched_energy: o?.matched_energy,
      })),
    [data, meters]
  );

  const transactionRows = useMemo(
    () =>
      (Array.isArray(data?.transactions) ? data.transactions : []).map((tx, i) => ({
        id: i,
        member_provider: memberName(tx?.provider_user_id),
        resource_provider: resourceName(tx?.provider_resource_id),
        member_receiver: memberName(tx?.receiver_user_id),
        resource_receiver: resourceName(tx?.receiver_resource_id),
        price: tx?.price,
        energy: tx?.energy,
        transaction_type: tx?.transaction_type || "—",
      })),
    [data, members, resources]
  );

  const offerColumns = [
    {
      key: "meter",
      label: t("offers.table.columns.meter"),
      width: "46%",
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    { key: "type", label: t("offers.table.columns.type"), width: "16%" },
    { key: "price", label: t("offers.table.columns.price"), align: "right", render: num },
    { key: "energy", label: t("offers.table.columns.energy"), align: "right", render: num },
    {
      key: "matched_energy",
      label: t("offers.table.columns.matched_energy"),
      align: "right",
      render: num,
    },
  ];

  const transactionColumns = [
    {
      key: "member_provider",
      label: t("transactions.table.columns.member_provider"),
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    {
      key: "resource_provider",
      label: t("transactions.table.columns.resource_provider"),
      render: (v) => <span style={{ color: "var(--ink-2)" }}>{v}</span>,
    },
    {
      key: "member_receiver",
      label: t("transactions.table.columns.member_receiver"),
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    {
      key: "resource_receiver",
      label: t("transactions.table.columns.resource_receiver"),
      render: (v) => <span style={{ color: "var(--ink-2)" }}>{v}</span>,
    },
    { key: "price", label: t("transactions.table.columns.price"), align: "right", render: num },
    { key: "energy", label: t("transactions.table.columns.energy"), align: "right", render: num },
    {
      key: "transaction_type",
      label: t("transactions.table.columns.transaction_type"),
      align: "right",
      render: (v) => <Chip tone="gray">{v}</Chip>,
    },
  ];

  const loadingText = t("table.loading", { ns: "common", defaultValue: "A carregar…" });

  return (
    <div className="rec-card overflow-hidden p-0">
      <div className="flex items-center gap-[14px] flex-wrap px-[var(--pad-card,20px)] py-[14px] border-b border-[var(--border)] bg-[var(--surface-2)]">
        <div className="flex items-center gap-[9px] flex-1 min-w-[200px] text-[14px] font-bold text-ink">
          <Icon name="storefront" style={{ color: "var(--accent)" }} />
          {t("single.header", { date: session.date, time: session.time })}
        </div>
        <Segmented
          value={detailTab}
          onChange={setDetailTab}
          options={[
            { value: "offers", label: `${t("single.tabs.offers")} (${offerRows.length})` },
            {
              value: "transactions",
              label: `${t("single.tabs.transactions")} (${transactionRows.length})`,
            },
          ]}
        />
        <button
          type="button"
          className="rec-btn-ghost rec-btn-sm"
          onClick={onClose}
          title={t("table.clear", { ns: "common", defaultValue: "Fechar" })}
        >
          <Icon name="close" />
        </button>
      </div>
      <div className="rec-tbl-fit">
        {detailTab === "offers" ? (
          <DataTable
            key="offers"
            columns={offerColumns}
            rows={offerRows}
            maxRows={8}
            footer={false}
            search={false}
            emptyText={isFetching ? loadingText : undefined}
          />
        ) : (
          <DataTable
            key="transactions"
            columns={transactionColumns}
            rows={transactionRows}
            maxRows={8}
            footer={false}
            search={false}
            emptyText={isFetching ? loadingText : undefined}
          />
        )}
      </div>
    </div>
  );
}

MarketSessionDetail.propTypes = {
  communityId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  resources: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  meters: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};

MarketSessionDetail.defaultProps = {
  members: [],
  resources: [],
  meters: [],
};

/* Market sessions for the period (server-paginated). Expanding a row reveals
   the per-session offers / transactions detail. */
function MarketSessionsTable() {
  const { t } = useTranslation(["market", "sessions", "common"]);
  const community = useSelector(selectCurrentCommunity);

  const [page, setPage] = useState(0);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [status, setStatus] = useState(null);
  const [openId, setOpenId] = useState(null);

  const { data, isFetching } = useGetSessionsByCommunityIdPaginationQuery(
    {
      communityId: community.id,
      page,
      limit: LIMIT,
      date,
      time,
      status,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
  );

  const members = useGetMembers("false");
  const resources = useGetResources("false");
  const meters = useGetMeters("false");

  const statusLabel = (value) => {
    if (value === "FAILED") return t("list.table.badges.warning", { ns: "sessions" });
    if (value === "NO MARKET" || value === "NO_TRANSACTIONS")
      return t("list.table.badges.no_market", { ns: "sessions" });
    if (value === "SETTLED") return t("list.table.badges.settled", { ns: "sessions" });
    if (value === "RUNNING") return t("list.table.badges.running", { ns: "sessions" });
    if (value === "DONE") return t("list.table.badges.done", { ns: "sessions" });
    return value;
  };

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((s, i) => ({
        id: s.id ?? i,
        date: formatDate(s.datetime_timestamp),
        time: datetimeToTime(s.datetime_timestamp),
        status: s.status,
        transaction_count: Number(s.transaction_count) || 0,
        offer_count: Number(s.offer_count) || 0,
        message: s.status,
      })),
    [data]
  );

  const openSession = useMemo(() => rows.find((r) => r.id === openId) || null, [rows, openId]);

  const allLabel = t("filters.all", { ns: "common", defaultValue: "Todos" });
  const dateOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: formatDate(o.date) })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...TIME_QUARTERS.map((q) => ({ value: q, label: q })),
  ];
  const statusOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_status ?? []).map((o) => ({
      value: o.status,
      label: statusLabel(o.status),
    })),
  ];

  const resetPage = () => {
    setPage(0);
    setOpenId(null);
  };

  const columns = [
    {
      key: "date",
      label: t("list.table.columns.date", { ns: "sessions" }),
      sortable: true,
      render: (v) => <b style={{ color: "var(--ink)" }}>{v}</b>,
    },
    {
      key: "time",
      label: t("list.table.columns.time", { ns: "sessions" }),
      sortable: true,
      render: (v) => <span className="tabular-nums text-ink-2">{v}</span>,
    },
    {
      key: "status",
      label: t("list.table.columns.status", { ns: "sessions" }),
      render: (v) => <Chip tone={statusTone(v)}>{statusLabel(v)}</Chip>,
    },
    {
      key: "transaction_count",
      label: t("list.table.columns.transaction_count"),
      align: "right",
      render: (v) => (
        <Chip tone={v > 0 ? "info" : "gray"}>
          {v} {t("list.table.badges.transactions_short")}
        </Chip>
      ),
    },
    {
      key: "offer_count",
      label: t("list.table.columns.offer_count"),
      align: "right",
      render: (v) => (
        <Chip tone={v > 0 ? "info" : "gray"}>
          {v} {t("list.table.badges.offers_short")}
        </Chip>
      ),
    },
    {
      key: "id",
      label: "",
      align: "right",
      render: (v) => (
        <button
          type="button"
          className="rec-btn-ghost rec-btn-sm"
          onClick={() => setOpenId((prev) => (prev === v ? null : v))}
          aria-expanded={openId === v}
        >
          <Icon
            name={openId === v ? "expand_less" : "chevron_right"}
            style={{ fontSize: 20, color: openId === v ? "var(--accent)" : "var(--ink-3)" }}
          />
        </button>
      ),
    },
  ];

  const filters = (
    <>
      <FieldSearchSelect
        label={t("list.table.columns.date", { ns: "sessions" })}
        value={date ?? ""}
        onChange={(v) => {
          resetPage();
          setDate(v || null);
        }}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("list.table.columns.time", { ns: "sessions" })}
        value={time ?? ""}
        onChange={(v) => {
          resetPage();
          setTime(v || null);
        }}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("list.table.columns.status", { ns: "sessions" })}
        value={status ?? ""}
        onChange={(v) => {
          resetPage();
          setStatus(v || null);
        }}
        options={statusOptions}
      />
    </>
  );

  return (
    <div className="flex flex-col gap-rec">
      <HelpFloatingButtonV2 helpScreen="market-list" />
      <div className="rec-card">
        <RemoteTable
          columns={columns}
          rows={rows}
          totalCount={data?.total_count ?? 0}
          page={page}
          limit={LIMIT}
          onPageChange={(p) => {
            setOpenId(null);
            setPage(p);
          }}
          sort={sortOrder}
          onSortChange={(s) => {
            setOpenId(null);
            setPage(0);
            setSortOrder(s);
          }}
          filters={filters}
          loading={isFetching}
        />
      </div>

      {openSession && (
        <MarketSessionDetail
          communityId={community.id}
          session={openSession}
          members={members}
          resources={resources}
          meters={meters}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

export default MarketSessionsTable;
